import { toast } from "$global-providers/toast-provider";
import {
  buildCurrencyExchange,
  type InvoiceCurrencyExchange,
} from "$routes/_lib/billing-v2/currency-exchange";
import { gql, useMutation } from "@apollo/client";
import { type BillingCurrency, TransactionType } from "elephant-web/gql";
import { useCallback, useState } from "react";
import { useIntl } from "react-intl";
import {
  type ExchangeFor,
  invoiceValuesOf,
  NO_EXCHANGE,
  rowExchange,
} from "../components/payment-rows";

type TransformedTransaction = {
  payer: {
    type: string;
    name: string;
  };
  amount: {
    value: number;
    currency: string;
  };
  payment?: {
    paymentMethod: string;
    authCode?: string;
    amount?: {
      value: number;
      currency: string;
    };
  };
  currencyExchange?: InvoiceCurrencyExchange;
};

export const TAKE_PAYMENT = gql`
  mutation takePayment($input: TakePaymentInput!) {
    takePayment(input: $input) {
      _id
      invoiceStatus
      transactions {
        _id
        actionedBy {
          _id
          uuid
          role_id
          email
          person {
            firstname
            middlename
            lastname
          }
        }
        type
        status
        timestamp
        notes
        payer {
          type
          name
        }
        amount {
          value
          currency
        }
        currencyExchange {
          from {
            currency
            value
          }
          to {
            currency
            value
          }
          exchangeRate {
            id
            baseCurrency
            quoteCurrency
            rate
            effectiveDate
          }
        }
        refund {
          paymentMethod
          authCode
          amount {
            value
            currency
          }
          timestamp
          reason
        }
        payment {
          paymentMethod
          authCode
          amount {
            value
            currency
          }
        }
      }
      payers {
        balance {
          total
          paid
          due
        }
      }
      balance {
        total
        paid
        due
        currency
      }
    }
  }
`;

type Transaction = {
  type?: string;
  amountInPence: number;
  method: string;
  authCode?: string;
  currency?: string;
};

export const transformTransactions = (
  transactions: Transaction[],
  currency: string,
  {
    exchangeFor,
    dueInPence,
  }: { exchangeFor: ExchangeFor; dueInPence: number } = {
    exchangeFor: NO_EXCHANGE,
    dueInPence: 0,
  },
): TransformedTransaction[] => {
  const invoiceValues = invoiceValuesOf(transactions, dueInPence, exchangeFor);

  return transactions.map((t, i) => {
    const exchange = rowExchange(t, exchangeFor);
    return {
      payer: {
        type: t.type || "PATIENT",
        name: t.type || "patient",
      },
      amount: {
        value: invoiceValues[i],
        currency,
      },
      payment:
        t.type === "WAIVER"
          ? undefined
          : {
              paymentMethod: t.method,
              authCode: t.authCode,
              amount: {
                value: invoiceValues[i],
                currency,
              },
            },
      ...(exchange && {
        currencyExchange: buildCurrencyExchange({
          type: TransactionType.Payment,
          invoiceSide: {
            currency: currency as BillingCurrency,
            value: invoiceValues[i],
          },
          exchanged: {
            currency: exchange.currency,
            value: t.amountInPence,
          },
          exchangeRate: exchange.rate,
        }),
      }),
    };
  });
};

export function useTakePayment(invoiceId: string) {
  const { formatMessage } = useIntl();
  const [saving, setSaving] = useState<boolean>(false);
  const [saved, setSaved] = useState<boolean>(false);

  const [makePayment] = useMutation(TAKE_PAYMENT, {
    onError(error) {
      toast.error(
        formatMessage(
          {
            id: "invoicing.takePayment.error.msg",
            defaultMessage: "Failed to record payment: {error}",
          },
          { error: error.message },
        ),
      );
    },
    onCompleted() {
      setSaving(false);
      setSaved(true);
    },
  });

  const takePayment = useCallback(
    async ({
      transactions,
      currency,
      exchange,
    }: {
      transactions: Transaction[];
      currency: string;
      exchange?: Parameters<typeof transformTransactions>[2];
    }) => {
      setSaving(true);
      await makePayment({
        variables: {
          input: {
            _id: invoiceId,
            transactions: transformTransactions(
              transactions,
              currency,
              exchange,
            ),
          },
        },
      });
    },
    [makePayment, invoiceId],
  );

  return { takePayment, saving, saved };
}
